import React, { useState, useEffect } from 'react';
import {
  Card,
  Form,
  Input,
  InputNumber,
  Button,
  Switch,
  Select,
  Space,
  Typography,
  Tabs,
  Divider,
  App,
  Modal,
  Row,
  Col,
  Radio,
  Checkbox,
  Alert
} from 'antd';
import {
  UserOutlined,
  SecurityScanOutlined,
  DatabaseOutlined,
  ApiOutlined,
  MailOutlined,
  EyeInvisibleOutlined,
  EyeTwoTone,
  InfoCircleOutlined,
  EditOutlined,
  EyeOutlined,
  SaveOutlined,
  SettingOutlined,
  AimOutlined,
  RobotOutlined,
  CheckCircleOutlined,
  DeleteOutlined,
  PlusOutlined,
  FileTextOutlined,
  CopyOutlined,
  ThunderboltOutlined
} from '@ant-design/icons';
import useAuthStore from '../../store/authStore';
import apiService from '../../services/apiService';
import TargetManagement from '../../components/TargetManagement';
import TenantAdminTargetsSettings from '../../components/TenantAdminTargetsSettings';
import LegacyRecordsSettings from '../../components/LegacyRecordsSettings';
import ApiKeysSettings from '../../components/ApiKeysSettings';
import DpdpaIntegrationSettings from '../../components/DpdpaIntegrationSettings';
import WhatsAppSettings from '../../components/WhatsAppSettings';
import BillingSettings from '../../components/BillingSettings';
import useSettingsStore from '../../store/settingsStore';
import aiSettingsService from '../../services/aiSettingsService';

const { Title, Text } = Typography;
const { Option } = Select;

const AdminSettingsPage = () => {
  const { message } = App.useApp();
  const { user } = useAuthStore();
  const { updateSettings, fetchSettings } = useSettingsStore();
  const [tenantSettings, setTenantSettings] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [apolloSettings, setApolloSettings] = useState(null);
  const [apolloLoading, setApolloLoading] = useState(false);
  const [emailSettings, setEmailSettings] = useState(null);
  const [emailLoading, setEmailLoading] = useState(false);
  const [templateModalVisible, setTemplateModalVisible] = useState(false);
  const [previewModalVisible, setPreviewModalVisible] = useState(false);
  const [currentTemplate, setCurrentTemplate] = useState(null);
  const [templatePreview, setTemplatePreview] = useState('');
  const [form] = Form.useForm();
  const [apolloForm] = Form.useForm();
  const [emailForm] = Form.useForm();
  const [templateForm] = Form.useForm();
  const [aiForm] = Form.useForm();
  const [aiCampaignForm] = Form.useForm();
  const [providers, setProviders] = useState([]);
  const [models, setModels] = useState([]);
  const [selectedProvider, setSelectedProvider] = useState(null);
  const [aiSettings, setAiSettings] = useState(null);
  const [campaigns, setCampaigns] = useState([]);
  const [editingCampaignId, setEditingCampaignId] = useState(null);
  const [showCampaignForm, setShowCampaignForm] = useState(false);
  const [aiLoading, setAiLoading] = useState(false);
  const [aiTesting, setAiTesting] = useState(false);
  const [companyProfileForm] = Form.useForm();
  const [companyProfile, setCompanyProfile] = useState(null);
  const [loadingProfile, setLoadingProfile] = useState(false);
  const [savingProfile, setSavingProfile] = useState(false);

  // Public lead form URL and iframe embed code, specific to this tenant
  const apiBaseUrl = ((process.env.REACT_APP_API_URL || `${window.location.origin}/api`)).replace(/\/$/, '');
  const tenantId = user?.tenantId;
  const publicLeadFormUrl = tenantId ? `${apiBaseUrl}/public-lead-form/${tenantId}` : '';
  const publicLeadFormIframeCode = tenantId
    ? `<iframe src="${publicLeadFormUrl}" width="100%" height="600" frameborder="0"></iframe>`
    : 'Tenant ID not available. Please ensure you are logged in as a tenant user.';

  useEffect(() => {
    fetchTenantSettings();
    fetchApolloSettings();
    fetchEmailSettings();
    fetchSettings(); // Load system settings
    fetchAiProviders();
    fetchCompanyProfile();
    if (user?.tenantId) {
      fetchAiSettings();
    }
  }, [fetchSettings, user?.tenantId]);

  const fetchCompanyProfile = async () => {
    try {
      setLoadingProfile(true);
      const response = await apiService.get('/settings/quotation-details');
      if (response.data.success && response.data.data) {
        setCompanyProfile(response.data.data);
        companyProfileForm.setFieldsValue(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching quotation details:', error);
    } finally {
      setLoadingProfile(false);
    }
  };

  const handleCompanyProfileSave = async (values) => {
    try {
      setSavingProfile(true);
      const response = await apiService.post('/settings/quotation-details', values);
      if (response.data.success) {
        message.success('Quotation details saved successfully');
        setCompanyProfile(response.data.data);
      } else {
        message.error(response.data.message || 'Failed to save quotation details');
      }
    } catch (error) {
      console.error('Error saving quotation details:', error);
      message.error(error.response?.data?.message || 'Failed to save quotation details');
    } finally {
      setSavingProfile(false);
    }
  };

  const fetchTenantSettings = async () => {
    setIsLoading(true);
    try {
      const response = await apiService.get('/admin/tenant/settings');
      const settings = response.data.data;
      setTenantSettings(settings);
      form.setFieldsValue(settings);
    } catch (error) {
      message.error('Failed to fetch tenant settings');
    } finally {
      setIsLoading(false);
    }
  };

  const fetchApolloSettings = async () => {
    setApolloLoading(true);
    try {
      const response = await apiService.get('/admin/integrations/apollo');
      const settings = response.data.data;
      setApolloSettings(settings);
    } catch (error) {
      const defaultSettings = {
        apiKey: '',
        enabled: false,
        syncInterval: 24,
        autoSync: false
      };
      setApolloSettings(defaultSettings);
    } finally {
      setApolloLoading(false);
    }
  };

  const fetchEmailSettings = async () => {
    setEmailLoading(true);
    try {
      const response = await apiService.get('/admin/email/settings');
      const settings = response.data.data;
      setEmailSettings(settings);
      emailForm.setFieldsValue(settings);
    } catch (error) {
      const defaultSettings = {
        smtpHost: '',
        smtpPort: 587,
        smtpUsername: '',
        smtpPassword: '',
        smtpSecure: false,
        senderName: '',
        senderEmail: '',
        enabled: false
      };
      setEmailSettings(defaultSettings);
      emailForm.setFieldsValue(defaultSettings);
    } finally {
      setEmailLoading(false);
    }
  };

  const handleSettingsUpdate = async (values) => {
    setIsLoading(true);
    try {
      console.log('Sending settings data:', values);
      await apiService.put('/admin/tenant/settings', values);
      
      // Update global settings if preferences changed
      if (values.preferences) {
        await updateSettings(values.preferences);
      }
      
      message.success('Settings updated successfully');
      await fetchTenantSettings();
      await fetchSettings(); // Refresh global settings
    } catch (error) {
      message.error('Failed to update settings');
      console.error('Settings update error:', error);
      console.error('Error response:', error.response?.data);
    } finally {
      setIsLoading(false);
    }
  };

  const handleApolloSettingsUpdate = async (values) => {
    setApolloLoading(true);
    try {
      await apiService.put('/admin/integrations/apollo', values);
      message.success('Apollo.io settings updated successfully');
      await fetchApolloSettings();
    } catch (error) {
      message.error('Failed to update Apollo.io settings');
    } finally {
      setApolloLoading(false);
    }
  };

  const handleEmailSettingsUpdate = async (values) => {
    setEmailLoading(true);
    try {
      await apiService.put('/admin/email/settings', values);
      message.success('Email settings updated successfully');
      await fetchEmailSettings();
    } catch (error) {
      message.error('Failed to update email settings');
    } finally {
      setEmailLoading(false);
    }
  };

  const testApolloConnection = async () => {
    setApolloLoading(true);
    try {
      await apiService.post('/admin/integrations/apollo/test');
      message.success('Apollo.io connection successful');
    } catch (error) {
      message.error('Apollo.io connection failed');
    } finally {
      setApolloLoading(false);
    }
  };

  const testEmailConnection = async () => {
    setEmailLoading(true);
    try {
      await apiService.post('/admin/email/test');
      message.success('Email connection successful');
    } catch (error) {
      message.error('Email connection failed');
    } finally {
      setEmailLoading(false);
    }
  };

  const sendTestEmail = async () => {
    setEmailLoading(true);
    try {
      await apiService.post('/admin/email/send-test');
      message.success('Test email sent successfully');
    } catch (error) {
      message.error('Failed to send test email');
    } finally {
      setEmailLoading(false);
    }
  };

  const editTemplate = (template) => {
    setCurrentTemplate(template);
    templateForm.setFieldsValue({
      subject: template.subject,
      body: template.body
    });
    setTemplateModalVisible(true);
  };

  const saveTemplate = async (values) => {
    try {
      await apiService.put(`/admin/email/templates/${currentTemplate.id}`, values);
      message.success('Template updated successfully');
      setTemplateModalVisible(false);
      await fetchEmailSettings();
    } catch (error) {
      message.error('Failed to update template');
    }
  };

  const previewTemplate = (template) => {
    setCurrentTemplate(template);
    setTemplatePreview(template.body);
    setPreviewModalVisible(true);
  };

  // AI Settings Functions
  const fetchAiProviders = async () => {
    try {
      const response = await aiSettingsService.getProviders();
      if (response.success) {
        setProviders(response.data);
      }
    } catch (error) {
      console.error('Error fetching providers:', error);
    }
  };

  const fetchAiSettings = async () => {
    try {
      const response = await aiSettingsService.getAiSettings(user?.tenantId);
      if (response.success) {
        setAiSettings(response.data);
        setCampaigns(response.data.campaigns || []);
        if (response.data.provider) {
          // The server never echoes the saved apiKey — `hasApiKey` flags
          // whether one is configured. Leave the input blank so the admin
          // either confirms (no change) or types a replacement.
          aiForm.setFieldsValue({
            provider: response.data.provider,
            model: response.data.model,
            apiKey: ''
          });
          setSelectedProvider(response.data.provider);
          fetchAiModels(response.data.provider);
        }
      }
    } catch (error) {
      console.error('Error fetching AI settings:', error);
    }
  };

  const fetchAiModels = async (provider) => {
    try {
      const response = await aiSettingsService.getModels(provider);
      if (response.success) {
        setModels(response.data);
      }
    } catch (error) {
      console.error('Error fetching models:', error);
    }
  };

  const handleProviderChange = (value) => {
    setSelectedProvider(value);
    aiForm.setFieldValue('model', null);
    fetchAiModels(value);
  };

  const handleSaveAiProvider = async (values) => {
    try {
      setAiLoading(true);
      const response = await aiSettingsService.updateProviderSettings(user?.tenantId, values);
      if (response.success) {
        message.success('AI provider settings saved successfully');
        setAiSettings(response.data);
      }
    } catch (error) {
      console.error('Error saving provider settings:', error);
      message.error('Failed to save provider settings');
    } finally {
      setAiLoading(false);
    }
  };

  const handleSaveCampaign = async (values) => {
    try {
      setAiLoading(true);
      
      // Only send allowed fields to backend
      const allowedFields = [
        'campaignTitle',
        'initialMessage',
        'productServiceName',
        'description',
        'keyFeatures',
        'pricingInformation',
        'primaryCallObjective',
        'bookCalendar',
        'successActionPlan',
        'aiGuidelinesRestrictions',
        'disconnectAction',
        'scheduleType'
      ];
      
      const cleanedValues = {};
      allowedFields.forEach(field => {
        if (field in values) {
          cleanedValues[field] = values[field];
        }
      });
      
      let response;
      if (editingCampaignId) {
        response = await aiSettingsService.updateCampaign(user?.tenantId, editingCampaignId, cleanedValues);
      } else {
        response = await aiSettingsService.createCampaign(user?.tenantId, cleanedValues);
      }
      
      if (response.success) {
        message.success(editingCampaignId ? 'Campaign updated successfully' : 'Campaign created successfully');
        setAiSettings(response.data);
        setCampaigns(response.data.campaigns || []);
        aiCampaignForm.resetFields();
        setShowCampaignForm(false);
        setEditingCampaignId(null);
      } else {
        message.error(response.message || 'Failed to save campaign');
      }
    } catch (error) {
      console.error('Error saving campaign:', error);
      message.error(error.message || 'Failed to save campaign');
    } finally {
      setAiLoading(false);
    }
  };

  const handleEditCampaign = (campaign) => {
    setEditingCampaignId(campaign._id);
    aiCampaignForm.setFieldsValue({
      campaignTitle: campaign.campaignTitle,
      initialMessage: campaign.initialMessage,
      productServiceName: campaign.productServiceName,
      description: campaign.description,
      keyFeatures: campaign.keyFeatures,
      pricingInformation: campaign.pricingInformation,
      primaryCallObjective: campaign.primaryCallObjective,
      bookCalendar: campaign.bookCalendar,
      successActionPlan: campaign.successActionPlan,
      aiGuidelinesRestrictions: campaign.aiGuidelinesRestrictions,
      disconnectAction: campaign.disconnectAction,
      scheduleType: campaign.scheduleType
    });
    setShowCampaignForm(true);
    // Scroll to the form after a short delay to ensure it's rendered
    setTimeout(() => {
      const formElement = document.querySelector('[data-campaign-form]');
      if (formElement) {
        formElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }, 100);
  };

  const handleDeleteCampaign = async (campaignId) => {
    try {
      setAiLoading(true);
      const response = await aiSettingsService.deleteCampaign(user?.tenantId, campaignId);
      if (response.success) {
        message.success('Campaign deleted successfully');
        setAiSettings(response.data);
        setCampaigns(response.data.campaigns || []);
      }
    } catch (error) {
      console.error('Error deleting campaign:', error);
      message.error('Failed to delete campaign');
    } finally {
      setAiLoading(false);
    }
  };

  const handleSetActiveCampaign = async (campaignId) => {
    try {
      setAiLoading(true);
      const response = await aiSettingsService.setActiveCampaign(user?.tenantId, campaignId);
      if (response.success) {
        message.success('Campaign set as active');
        setAiSettings(response.data);
        setCampaigns(response.data.campaigns || []);
      }
    } catch (error) {
      console.error('Error setting active campaign:', error);
      message.error('Failed to set active campaign');
    } finally {
      setAiLoading(false);
    }
  };

  const handleCancelCampaignForm = () => {
    setShowCampaignForm(false);
    setEditingCampaignId(null);
    aiCampaignForm.resetFields();
  };

  const handleCopyPublicLeadFormUrl = async () => {
    if (!publicLeadFormUrl) return;
    try {
      await navigator.clipboard.writeText(publicLeadFormUrl);
      message.success('Public lead form URL copied');
    } catch (error) {
      message.error('Failed to copy URL');
    }
  };

  const handleCopyPublicLeadFormIframe = async () => {
    if (!publicLeadFormIframeCode) return;
    try {
      await navigator.clipboard.writeText(publicLeadFormIframeCode);
      message.success('Iframe code copied');
    } catch (error) {
      message.error('Failed to copy iframe code');
    }
  };

  const tabItems = [
    {
      key: 'quotation-details',
      forceRender: true,
      label: (
        <span>
          <FileTextOutlined />
          Quotation Details
        </span>
      ),
      children: (
        <Form
          form={companyProfileForm}
          layout="vertical"
          onFinish={handleCompanyProfileSave}
        >
          <div className="space-y-4">
            <div>
              <Title level={5} className="mb-4">
                <FileTextOutlined className="mr-2" />
                Company Information for Quotations
              </Title>
              <Text className="text-gray-600 dark:text-gray-400 block mb-6">
                This information will be used in automatically generated quotations and proposals
              </Text>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Form.Item
                name="companyName"
                label="Company Name"
                rules={[{ required: true, message: 'Please enter company name' }]}
              >
                <Input placeholder="Enter your company name" />
              </Form.Item>

              <Form.Item
                name="yourName"
                label="Your Name"
                rules={[{ required: true, message: 'Please enter your name' }]}
              >
                <Input placeholder="Enter your full name" />
              </Form.Item>

              <Form.Item
                name="email"
                label="Email Address"
                rules={[
                  { required: true, message: 'Please enter email' },
                  { type: 'email', message: 'Please enter valid email' }
                ]}
              >
                <Input placeholder="your.email@company.com" />
              </Form.Item>

              <Form.Item
                name="phone"
                label="Phone Number"
                rules={[{ required: true, message: 'Please enter phone number' }]}
              >
                <Input placeholder="+1 (555) 123-4567" />
              </Form.Item>
            </div>

            <Form.Item
              name="address"
              label="Address"
              rules={[{ required: true, message: 'Please enter address' }]}
            >
              <Input placeholder="Street address" />
            </Form.Item>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <Form.Item
                name="city"
                label="City"
                rules={[{ required: true, message: 'Please enter city' }]}
              >
                <Input placeholder="City" />
              </Form.Item>

              <Form.Item
                name="state"
                label="State/Province"
                rules={[{ required: true, message: 'Please enter state' }]}
              >
                <Input placeholder="State" />
              </Form.Item>

              <Form.Item
                name="zipCode"
                label="ZIP/Postal Code"
                rules={[{ required: true, message: 'Please enter ZIP code' }]}
              >
                <Input placeholder="ZIP Code" />
              </Form.Item>
            </div>

            <Divider className="my-6" />

            <div>
              <Title level={5} className="mb-4">
                Additional Information (Optional)
              </Title>
              <Text className="text-gray-600 dark:text-gray-400 block mb-6">
                These details will enhance your proposals and quotations
              </Text>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Form.Item
                name="website"
                label="Website"
                rules={[
                  { type: 'url', message: 'Please enter a valid URL' }
                ]}
              >
                <Input placeholder="https://www.yourcompany.com" />
              </Form.Item>

              <Form.Item
                name="taxId"
                label="Tax ID / Registration Number"
              >
                <Input placeholder="Enter tax ID or registration number" />
              </Form.Item>
            </div>

            <Form.Item
              name="companyDescription"
              label="Company Description / Tagline"
            >
              <Input.TextArea 
                rows={3}
                maxLength={500}
                showCount
                placeholder="Brief description of your company or a tagline (max 500 characters)"
              />
            </Form.Item>

            <Form.Item
              name="logoUrl"
              label="Company Logo URL"
              rules={[
                { type: 'url', message: 'Please enter a valid URL' }
              ]}
            >
              <Input placeholder="https://www.yourcompany.com/logo.png" />
            </Form.Item>

            <Divider className="my-6" />

            <div className="bg-blue-50 dark:bg-blue-900/20 p-4 rounded-lg mb-6">
              <Text className="text-sm text-blue-600 dark:text-blue-400">
                <strong>Note:</strong> This information will be automatically included in all quotations and proposals generated from the CRM. 
                Make sure to keep it up to date for professional-looking documents.
              </Text>
            </div>
          </div>

          <Form.Item>
            <Button 
              type="primary" 
              htmlType="submit" 
              loading={savingProfile}
              icon={<SaveOutlined />}
              className="focus-visible:focus"
            >
              Save Quotation Details
            </Button>
          </Form.Item>
        </Form>
      )
    },
    {
      key: 'general',
      forceRender: true,
      label: (
        <span>
          <SettingOutlined />
          General
        </span>
      ),
      children: (
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSettingsUpdate}
          initialValues={tenantSettings}
        >
          <Card title="Organization Information" className="mb-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Form.Item
                name={['organization', 'name']}
                label="Organization Name"
                rules={[{ required: true, message: 'Organization name is required' }]}
              >
                <Input placeholder="Enter organization name" />
              </Form.Item>

              <Form.Item
                name={['organization', 'subdomain']}
                label="Subdomain"
                rules={[{ required: true, message: 'Subdomain is required' }]}
              >
                <Input 
                  placeholder="Enter subdomain" 
                  addonAfter=".crm.com"
                />
              </Form.Item>

              <Form.Item
                name={['organization', 'industry']}
                label="Industry"
              >
                <Select placeholder="Select industry">
                  <Option value="technology">Technology</Option>
                  <Option value="healthcare">Healthcare</Option>
                  <Option value="finance">Finance</Option>
                  <Option value="retail">Retail</Option>
                  <Option value="manufacturing">Manufacturing</Option>
                  <Option value="other">Other</Option>
                </Select>
              </Form.Item>

              <Form.Item
                name={['organization', 'size']}
                label="Company Size"
              >
                <Select placeholder="Select company size">
                  <Option value="1-10">1-10 employees</Option>
                  <Option value="11-50">11-50 employees</Option>
                  <Option value="51-200">51-200 employees</Option>
                  <Option value="201-500">201-500 employees</Option>
                  <Option value="500+">500+ employees</Option>
                </Select>
              </Form.Item>
            </div>
          </Card>

          <Card title="System Preferences" className="mb-6">
            <div className="space-y-4">
              <div className="flex justify-between items-center">
                <div>
                  <Text className="block font-medium">Time Zone</Text>
                  <Text className="text-sm text-gray-500 dark:text-gray-400">
                    Default time zone for the organization
                  </Text>
                </div>
                <Form.Item name={['preferences', 'timezone']} className="mb-0">
                  <Select style={{ width: 200 }}>
                    <Option value="UTC">UTC</Option>
                    <Option value="America/New_York">Eastern Time</Option>
                    <Option value="America/Los_Angeles">Pacific Time</Option>
                    <Option value="Europe/London">London</Option>
                    <Option value="Asia/Kolkata">India</Option>
                  </Select>
                </Form.Item>
              </div>

              <div className="flex justify-between items-center">
                <div>
                  <Text className="block font-medium">Date Format</Text>
                  <Text className="text-sm text-gray-500 dark:text-gray-400">
                    How dates are displayed throughout the system
                  </Text>
                </div>
                <Form.Item name={['preferences', 'dateFormat']} className="mb-0">
                  <Select style={{ width: 150 }}>
                    <Option value="MM/DD/YYYY">MM/DD/YYYY</Option>
                    <Option value="DD/MM/YYYY">DD/MM/YYYY</Option>
                    <Option value="YYYY-MM-DD">YYYY-MM-DD</Option>
                  </Select>
                </Form.Item>
              </div>
            </div>
          </Card>

          <Form.Item>
            <Button 
              type="primary" 
              htmlType="submit" 
              loading={isLoading}
              icon={<SaveOutlined />}
              className="focus-visible:focus"
            >
              Save General Settings
            </Button>
          </Form.Item>
        </Form>
      )
    },
    {
      key: 'targets',
      label: (
        <span>
          <AimOutlined />
          Targets
        </span>
      ),
      children: user?.role === 'tenant_admin'
        ? <TenantAdminTargetsSettings />
        : <TargetManagement />
    },
    {
      key: 'email',
      forceRender: true,
      label: (
        <span>
          <MailOutlined />
          Email Configuration
        </span>
      ),
      children: (
        <Form
          form={emailForm}
          layout="vertical"
          onFinish={handleEmailSettingsUpdate}
          initialValues={emailSettings}
        >
          <Card title="SMTP Configuration" className="mb-6">
            <div className="mb-4">
              <Text className="text-gray-600 dark:text-gray-400">
                Configure your email server settings to send emails from the CRM system.
                These settings will override the default .env configuration.
              </Text>
            </div>

            <div className="flex justify-between items-center mb-6 p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
              <div>
                <Text className="block font-medium">Enable Email Service</Text>
                <Text className="text-sm text-gray-500 dark:text-gray-400">
                  Turn on to use custom SMTP settings instead of .env defaults
                </Text>
              </div>
              <Form.Item name="enabled" valuePropName="checked" className="mb-0">
                <Switch />
              </Form.Item>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Form.Item
                name={['smtp', 'host']}
                label="SMTP Host"
                rules={[{ required: true, message: 'SMTP host is required' }]}
              >
                <Input 
                  placeholder="smtp.gmail.com" 
                  prefix={<DatabaseOutlined />}
                />
              </Form.Item>

              <Form.Item
                name={['smtp', 'port']}
                label="SMTP Port"
                rules={[{ required: true, message: 'SMTP port is required' }]}
              >
                <InputNumber 
                  placeholder="587" 
                  min={1} 
                  max={65535}
                  style={{ width: '100%' }}
                />
              </Form.Item>

              <Form.Item
                name={['smtp', 'username']}
                label="SMTP Username"
                rules={[{ required: true, message: 'SMTP username is required' }]}
              >
                <Input 
                  placeholder="your-email@gmail.com" 
                  prefix={<UserOutlined />}
                />
              </Form.Item>

              <Form.Item
                name={['smtp', 'password']}
                label="SMTP Password"
                rules={[{ required: true, message: 'SMTP password is required' }]}
              >
                <Input.Password 
                  placeholder="Enter SMTP password or app password"
                  iconRender={visible => (visible ? <EyeTwoTone /> : <EyeInvisibleOutlined />)}
                />
              </Form.Item>

              <Form.Item
                name={['sender', 'name']}
                label="Sender Name"
                rules={[{ required: true, message: 'Sender name is required' }]}
              >
                <Input 
                  placeholder="Your Company Name" 
                  prefix={<UserOutlined />}
                />
              </Form.Item>

              <Form.Item
                name={['sender', 'email']}
                label="Sender Email"
                rules={[
                  { required: true, message: 'Sender email is required' },
                  { type: 'email', message: 'Please enter a valid email' }
                ]}
              >
                <Input 
                  placeholder="noreply@yourcompany.com" 
                  prefix={<MailOutlined />}
                />
              </Form.Item>
            </div>

            <div className="flex items-center mt-4">
              <Form.Item name={['smtp', 'secure']} valuePropName="checked" className="mb-0">
                <Switch />
              </Form.Item>
              <div className="ml-3">
                <Text className="block font-medium">Use SSL/TLS</Text>
                <Text className="text-sm text-gray-500 dark:text-gray-400">
                  Enable for secure connection (recommended for port 465)
                </Text>
              </div>
            </div>
          </Card>

          <Card title="Email Testing" className="mb-6">
            <div className="space-y-4">
              <div>
                <Text className="block mb-2 text-gray-600 dark:text-gray-400">
                  Test your email configuration to ensure it's working correctly.
                </Text>
              </div>
              <Space>
                <Button 
                  onClick={testEmailConnection}
                  loading={emailLoading}
                  icon={<ApiOutlined />}
                >
                  Test Connection
                </Button>
                <Button 
                  onClick={sendTestEmail}
                  loading={emailLoading}
                  icon={<MailOutlined />}
                >
                  Send Test Email
                </Button>
              </Space>
            </div>
          </Card>

          <Form.Item>
            <Space>
              <Button 
                type="primary" 
                htmlType="submit" 
                loading={emailLoading}
                icon={<SaveOutlined />}
              >
                Save Email Settings
              </Button>
              <Button onClick={() => emailForm.resetFields()}>
                Reset
              </Button>
            </Space>
          </Form.Item>
        </Form>
      )
    },
    {
      key: 'ai-settings',
      forceRender: true,
      label: (
        <span>
          <RobotOutlined />
          AI Settings
        </span>
      ),
      children: (
        <div style={{ padding: '24px' }}>
          {/* Provider Settings Section */}
          <Card title="AI Provider Settings" style={{ marginBottom: 24 }}>
            <Form
              form={aiForm}
              layout="vertical"
              onFinish={handleSaveAiProvider}
              disabled={aiLoading}
            >
              <Row gutter={24}>
                <Col xs={24} sm={12}>
                  <Form.Item
                    label="AI Provider"
                    name="provider"
                    rules={[{ required: true, message: 'Please select an AI provider' }]}
                  >
                    <Select
                      placeholder="Select AI Provider"
                      onChange={handleProviderChange}
                      options={providers.map(p => ({
                        label: p.provider,
                        value: p.provider
                      }))}
                    />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={12}>
                  <Form.Item
                    label="Model"
                    name="model"
                    rules={[{ required: true, message: 'Please select a model' }]}
                  >
                    <Select
                      placeholder="Select Model"
                      disabled={!selectedProvider}
                      options={models.map(m => ({
                        label: m,
                        value: m
                      }))}
                    />
                  </Form.Item>
                </Col>
              </Row>

              <Form.Item
                label="API Key"
                name="apiKey"
                tooltip={aiSettings?.hasApiKey ? 'Leave blank to keep the existing key' : undefined}
                rules={
                  aiSettings?.hasApiKey
                    ? []
                    : [{ required: true, message: 'Please enter API key' }]
                }
              >
                <Input.Password
                  placeholder={aiSettings?.hasApiKey ? '•••• key on file — leave blank to keep' : 'Enter your API key'}
                  autoComplete="new-password"
                />
              </Form.Item>

              <Form.Item>
                <Space>
                  <Button type="primary" htmlType="submit" icon={<SaveOutlined />} loading={aiLoading}>
                    Save Provider Settings
                  </Button>
                  <Button
                    icon={<ThunderboltOutlined />}
                    loading={aiTesting}
                    disabled={!aiSettings?.hasApiKey && !aiSettings?.apiKey}
                    onClick={async () => {
                      setAiTesting(true);
                      try {
                        const res = await aiSettingsService.testProvider(user?.tenantId);
                        if (res.success) {
                          message.success(`AI ready: ${res.model || 'connected'}.${res.sample ? ` Sample: ${res.sample}` : ''}`);
                        } else {
                          message.error(res.message || 'AI test failed');
                        }
                      } catch (err) {
                        message.error(err?.message || err?.response?.data?.message || 'AI test failed');
                      } finally {
                        setAiTesting(false);
                      }
                    }}
                  >
                    Test Connection
                  </Button>
                </Space>
              </Form.Item>
            </Form>
          </Card>

          {/* Campaigns Section */}
          <Card title="AI Calling Campaigns" style={{ marginBottom: 24 }}>
            <div style={{ marginBottom: 16 }}>
              {!showCampaignForm && (
                <Button
                  type="primary"
                  icon={<PlusOutlined />}
                  onClick={() => setShowCampaignForm(true)}
                >
                  Create New Campaign
                </Button>
              )}
            </div>

            <Modal
              title={editingCampaignId ? 'Edit Campaign' : 'Create New Campaign'}
              open={showCampaignForm}
              onCancel={handleCancelCampaignForm}
              footer={null}
              width={800}
              styles={{ body: { maxHeight: '70vh', overflowY: 'auto' } }}
            >
              <Form
                form={aiCampaignForm}
                layout="vertical"
                onFinish={handleSaveCampaign}
              >
                <Row gutter={24}>
                  <Col xs={24} sm={12}>
                    <Form.Item
                      label="Campaign Title"
                      name="campaignTitle"
                      rules={[{ required: true, message: 'Campaign title is required' }]}
                    >
                      <Input placeholder="Enter campaign title" />
                    </Form.Item>
                  </Col>
                  <Col xs={24} sm={12}>
                    <Form.Item
                      label="Product/Service Name"
                      name="productServiceName"
                      rules={[{ required: true, message: 'Product/Service name is required' }]}
                    >
                      <Input placeholder="Enter product or service name" />
                    </Form.Item>
                  </Col>
                </Row>

                <Form.Item
                  label="Initial Message"
                  name="initialMessage"
                  rules={[{ required: true, message: 'Initial message is required' }]}
                >
                  <Input.TextArea rows={3} placeholder="Enter the initial message for the call" />
                </Form.Item>

                <Form.Item
                  label="Description"
                  name="description"
                  rules={[{ required: true, message: 'Description is required' }]}
                >
                  <Input.TextArea rows={2} placeholder="Enter campaign description" />
                </Form.Item>

                <Form.Item
                  label="Key Features"
                  name="keyFeatures"
                  rules={[{ required: true, message: 'Key features are required' }]}
                >
                  <Input.TextArea rows={2} placeholder="List key features" />
                </Form.Item>

                <Form.Item
                  label="Pricing Information"
                  name="pricingInformation"
                >
                  <Input.TextArea rows={2} placeholder="Enter pricing information (optional)" />
                </Form.Item>

                <Form.Item
                  label="Primary Call Objective"
                  name="primaryCallObjective"
                  rules={[{ required: true, message: 'Primary call objective is required' }]}
                >
                  <Input.TextArea rows={2} placeholder="What is the main objective of the call?" />
                </Form.Item>

                <Form.Item
                  label="Success Action Plan"
                  name="successActionPlan"
                  rules={[{ required: true, message: 'Success action plan is required' }]}
                >
                  <Input.TextArea rows={2} placeholder="What happens on successful call?" />
                </Form.Item>

                <Form.Item
                  label="AI Guidelines & Restrictions"
                  name="aiGuidelinesRestrictions"
                  rules={[{ required: true, message: 'AI guidelines are required' }]}
                >
                  <Input.TextArea rows={2} placeholder="Any guidelines or restrictions for the AI?" />
                </Form.Item>

                <Form.Item
                  label="Disconnect Action"
                  name="disconnectAction"
                  rules={[{ required: true, message: 'Disconnect action is required' }]}
                >
                  <Input.TextArea rows={2} placeholder="What should happen when the call is disconnected?" />
                </Form.Item>

                <Row gutter={24}>
                  <Col xs={24} sm={12}>
                    <Form.Item
                      label="Schedule Type"
                      name="scheduleType"
                      rules={[{ required: true, message: 'Schedule type is required' }]}
                    >
                      <Radio.Group>
                        <Radio value="immediate">Immediate</Radio>
                        <Radio value="schedule_later">Schedule Later</Radio>
                      </Radio.Group>
                    </Form.Item>
                  </Col>
                  <Col xs={24} sm={12}>
                    <Form.Item
                      label="Book Calendar"
                      name="bookCalendar"
                      valuePropName="checked"
                    >
                      <Checkbox>Enable calendar booking</Checkbox>
                    </Form.Item>
                  </Col>
                </Row>

                <Form.Item>
                  <Space>
                    <Button type="primary" htmlType="submit" loading={aiLoading}>
                      {editingCampaignId ? 'Update Campaign' : 'Create Campaign'}
                    </Button>
                    <Button onClick={handleCancelCampaignForm}>Cancel</Button>
                  </Space>
                </Form.Item>
              </Form>
            </Modal>

            {/* Campaigns List */}
            {campaigns.length > 0 ? (
              <div>
                <Divider />
                <Title level={5}>Active Campaigns ({campaigns.length})</Title>
                {campaigns.map(campaign => (
                  <Card
                    key={campaign._id}
                    style={{ marginBottom: 16 }}
                    title={campaign.campaignTitle}
                    extra={
                      <Space>
                        <Button
                          type={campaign.isActive ? 'primary' : 'default'}
                          size="small"
                          icon={<CheckCircleOutlined />}
                          onClick={() => handleSetActiveCampaign(campaign._id)}
                        >
                          {campaign.isActive ? 'Active' : 'Set Active'}
                        </Button>
                        <Button
                          type="primary"
                          size="small"
                          onClick={() => handleEditCampaign(campaign)}
                        >
                          Edit
                        </Button>
                        <Button
                          danger
                          size="small"
                          icon={<DeleteOutlined />}
                          onClick={() => handleDeleteCampaign(campaign._id)}
                        >
                          Delete
                        </Button>
                      </Space>
                    }
                  >
                    <Row gutter={24}>
                      <Col xs={24} sm={12}>
                        <div style={{ marginBottom: 12 }}>
                          <Text strong>Product/Service:</Text>
                          <div>{campaign.productServiceName}</div>
                        </div>
                        <div style={{ marginBottom: 12 }}>
                          <Text strong>Schedule Type:</Text>
                          <div>{campaign.scheduleType === 'immediate' ? 'Immediate' : 'Schedule Later'}</div>
                        </div>
                      </Col>
                      <Col xs={24} sm={12}>
                        <div style={{ marginBottom: 12 }}>
                          <Text strong>Book Calendar:</Text>
                          <div>{campaign.bookCalendar ? 'Yes' : 'No'}</div>
                        </div>
                        <div style={{ marginBottom: 12 }}>
                          <Text strong>Created:</Text>
                          <div>{new Date(campaign.createdAt).toLocaleDateString()}</div>
                        </div>
                      </Col>
                    </Row>
                    <Divider />
                    <Row gutter={24}>
                      <Col xs={24} sm={12}>
                        <Text strong>Description:</Text>
                        <div style={{ marginBottom: 12 }}>{campaign.description}</div>
                      </Col>
                      <Col xs={24} sm={12}>
                        <Text strong>Primary Call Objective:</Text>
                        <div>{campaign.primaryCallObjective}</div>
                      </Col>
                    </Row>
                  </Card>
                ))}
              </div>
            ) : (
              <Alert
                message="No campaigns created yet"
                description="Create your first AI calling campaign to get started"
                type="info"
                showIcon
              />
            )}
          </Card>
        </div>
      )
    },
    {
      key: 'billing',
      label: 'Billing',
      children: <BillingSettings />
    },
    {
      key: 'whatsapp',
      label: 'WhatsApp',
      children: <WhatsAppSettings />
    },
    {
      key: 'legacy-records',
      label: 'Legacy Records',
      children: <LegacyRecordsSettings />
    },
    {
      key: 'lead-form',
      label: 'Lead Form',
      children: (
        <Card title="Embeddable Lead Capture Form" className="mb-6">
          <div className="space-y-4">
            <Text className="text-gray-600 dark:text-gray-400 block">
              Use this public lead form on your website or landing pages. Anyone who submits the form will be created as a lead for this tenant. No authentication is required.
            </Text>
            {!tenantId && (
              <Alert
                type="warning"
                message="Tenant information is not available."
                description="Please ensure you are logged in as a tenant user to see the embed code."
                showIcon
              />
            )}
            {tenantId && (
              <>
                <Form layout="vertical">
                  <Form.Item label="Public Lead Form URL">
                    <Space direction="vertical" style={{ width: '100%' }}>
                      <Input.TextArea
                        rows={2}
                        value={publicLeadFormUrl}
                        readOnly
                        disabled
                        style={{
                          fontFamily: 'SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace',
                          backgroundColor: '#0f172a',
                          color: '#e5e7eb',
                          borderRadius: 6,
                          opacity: 0.9,
                        }}
                      />
                      <Button
                        type="default"
                        size="small"
                        icon={<CopyOutlined />}
                        onClick={handleCopyPublicLeadFormUrl}
                        style={{ alignSelf: 'flex-end' }}
                      >
                        Copy URL
                      </Button>
                    </Space>
                  </Form.Item>
                  <Form.Item label="Iframe Embed Code">
                    <Space direction="vertical" style={{ width: '100%' }}>
                      <Input.TextArea
                        rows={6}
                        value={publicLeadFormIframeCode}
                        readOnly
                        disabled
                        style={{
                          fontFamily: 'SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace',
                          backgroundColor: '#0f172a',
                          color: '#e5e7eb',
                          borderRadius: 6,
                          opacity: 0.9,
                        }}
                      />
                      <Button
                        type="default"
                        size="small"
                        icon={<CopyOutlined />}
                        onClick={handleCopyPublicLeadFormIframe}
                        style={{ alignSelf: 'flex-end' }}
                      >
                        Copy iframe
                      </Button>
                    </Space>
                  </Form.Item>
                </Form>
                <Alert
                  type="info"
                  showIcon
                  message="How to use"
                  description="Copy the iframe code above and paste it into the HTML of any page where you want to show the lead form. Submissions will automatically create leads in this tenant, with the query saved as a note on the lead."
                />
              </>
            )}
          </div>
        </Card>
      )
    },
    {
      key: 'api-keys',
      label: (
        <span>
          <ApiOutlined />
          API Keys
        </span>
      ),
      children: (
        <Card bordered={false}>
          <ApiKeysSettings />
        </Card>
      )
    },
    {
      key: 'dpdpa',
      label: (
        <span>
          <ApiOutlined />
          DPDPA
        </span>
      ),
      children: (
        <Card bordered={false}>
          <DpdpaIntegrationSettings />
        </Card>
      )
    }
  ];

  return (
    <App>
      <div className="space-y-6">
        <div>
          <Title level={2} className="mb-2">Admin Settings</Title>
          <Text className="text-gray-600 dark:text-gray-400">
            Configure system settings and tenant preferences
          </Text>
        </div>

        <Tabs 
          defaultActiveKey="general" 
          className="bg-white dark:bg-gray-800 rounded-lg shadow-soft p-6"
          items={tabItems}
        />
      </div>
    </App>
  );
};

export default AdminSettingsPage;
